import { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

import FilterChip from 'LuxKit/Chips/FilterChip'
import { EmptyArray } from 'lib/array/arrayUtils'
import { FilterIcons, FilterLabels } from 'tripPlanner/models'
import type { TripPlannerFilterTypeCount } from 'tripPlanner/utils/summary'
import LineSlidersVIcon from 'LuxKit/Icons/line/LineSlidersVIcon'
import useStableObjectReference from 'hooks/useStableObjectReference'

interface Props {
  itemCount: number
  availableSubFilters: Array<TripPlannerFilterTypeCount>
  selectedFilters: Array<App.TripPlannerSubFilterType>
  onClearFilters: () => void
  onToggleFilter: (filterType: App.TripPlannerSubFilterType) => void
}

function FilterChips({
  availableSubFilters = EmptyArray,
  selectedFilters,
  itemCount,
  onClearFilters,
  onToggleFilter,
}: Props) {
  const intl = useIntl()
  // Avoid triggering the effect when the reference changes without the filters actually changing
  const availableSubFiltersStabilised = useStableObjectReference(availableSubFilters)

  useEffect(() => {
    onClearFilters()
  }, [availableSubFiltersStabilised, onClearFilters])

  const selectedFiltersSet = useMemo(
    () => new Set(selectedFilters),
    [selectedFilters],
  )

  return (
    <>
      <FilterChip
        key="allItems"
        data-testid="trip-filters-all"
        onClick={onClearFilters}
        size="medium"
        startIcon={<LineSlidersVIcon />}
        selected={selectedFiltersSet.size === 0}
      >
        {intl.formatMessage({
          id: 'L3MbAE-rMKqw8M4QBdEA',
          defaultMessage: 'All Items · {count}',
          description: 'Filter chip label showing total count of all trip items',
        }, { count: itemCount })}
      </FilterChip>
      {availableSubFilters.map((filter: TripPlannerFilterTypeCount) => {
        const { filterType, count } = filter
        const Icon = FilterIcons[filterType]

        return (
          <FilterChip
            key={filterType}
            selected={selectedFiltersSet.has(filterType)}
            size="medium"
            startIcon={<Icon />}
            data-testid={'trip-filters-' + filterType}
            onClick={() => onToggleFilter(filterType)}
          >
            {intl.formatMessage(
              {
                id: 'Ln5j-vljQ8R-7tqKmMLq',
                defaultMessage: '{label} · {count}',
                description: 'Trip planner filter chip showing item type label and count',
              },
              { label: intl.formatMessage(FilterLabels[filterType]), count },
            )}
          </FilterChip>
        )
      })}
    </>
  )
}

export default FilterChips
